type ChartPointValue = number | string | boolean | null | undefined;

interface ChartPointObject {
  value?: ChartPointValue;
  [key: string]: unknown;
}

type ChartPoint = ChartPointValue | ChartPointObject;

type ChartSeries = ChartPoint[];

const isPlainObject = (value: unknown): value is ChartPointObject => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
};

const getChartPointValue = (point: ChartPoint): ChartPointValue => {
  if (!isPlainObject(point)) {
    return point;
  }

  return point.value;
};

const isMissingChartValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === false) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (isPlainObject(value)) {
    if ('value' in value) {
      return isMissingChartValue(value.value);
    }

    return Object.keys(value).length === 0;
  }

  return false;
};

const isSeriesWithDisplayablePoint = (series: unknown): boolean => {
  if (!Array.isArray(series) || series.length === 0) {
    return false;
  }

  return series.some((point: ChartPoint) => {
    return !isMissingChartValue(getChartPointValue(point));
  });
};

const getNormalizedSeriesValues = (series: unknown): ChartPointObject[] => {
  if (!Array.isArray(series)) {
    return [];
  }

  return series.map((point: ChartPoint) => {
    if (!isPlainObject(point)) {
      return { value: point };
    }

    const pointValue = getChartPointValue(point);
    if (isMissingChartValue(pointValue)) {
      return { ...point, value: null };
    }

    return { ...point, value: pointValue };
  });
};

const isAverageDisplayable = (average: unknown): boolean => {
  return !isMissingChartValue(average);
};

export {
  isAverageDisplayable,
  getChartPointValue,
  isMissingChartValue,
  getNormalizedSeriesValues,
  isSeriesWithDisplayablePoint,
  isAverageDisplayable as averageIsDisplayable,
  getNormalizedSeriesValues as normalizeSeriesValues,
  isSeriesWithDisplayablePoint as seriesHasDisplayablePoint,
};

export type { ChartPoint, ChartPointObject, ChartPointValue, ChartSeries };
